import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { CATEGORY_LABELS } from "./dpi";
import { TOOL_SUBCATEGORIES } from "../lib/categories";
import PhotoUploader from "./PhotoUploader";
import DocumentUploader from "./DocumentUploader";
import ManualPicker from "./ManualPicker";
import { X } from "lucide-react";

const BRAND_OPTIONS = ["Irudek", "Kask", "Kong", "Vertiqual"];

export default function DeviceDialog({ open, initial, onClose, onSaved, onCreated, kind = "dpi", initialClientId = "" }) {
  const isTool = kind === "strumento";
  const defaultCategory = isTool ? TOOL_SUBCATEGORIES[0].value : "casco";
  const categoryOptions = isTool
    ? TOOL_SUBCATEGORIES.map((s) => [s.value, s.label])
    : Object.entries(CATEGORY_LABELS);
  const [form, setForm] = useState({
    category: defaultCategory,
    client_id: initialClientId || "",
    dipendente: "",
    brand: "",
    brand_other: "",
    model: "",
    serial_number: "",
    notes: "",
    photos: [],
    documents: [],
    manual_ids: [],
    production_date: "",
    first_use_date: "",
    last_inspection_date: "",
    useful_life_years: "",
  });
  const [clients, setClients] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [similarNotice, setSimilarNotice] = useState("");

  const isEdit = Boolean(initial);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/clients");
        setClients(data);
      } catch {
        /* admin only */
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      const rawBrand = initial.brand || "";
      const isStandard = BRAND_OPTIONS.includes(rawBrand);
      setForm({
        category: initial.category || "casco",
        client_id: initial.client_id || "",
        dipendente: initial.dipendente || "",
        brand: rawBrand === "" ? "" : isStandard ? rawBrand : "altro",
        brand_other: rawBrand && !isStandard ? rawBrand : "",
        model: initial.model || "",
        serial_number: initial.serial_number || "",
        notes: initial.notes || "",
        photos: initial.photos || [],
        documents: (initial.documents || []).map((d) => (typeof d === "string" ? { path: d } : d)),
        manual_ids: initial.manual_ids || [],
        production_date: initial.production_date || "",
        first_use_date: initial.first_use_date || "",
        last_inspection_date: initial.last_inspection_date || "",
        useful_life_years: initial.useful_life_years != null ? String(initial.useful_life_years) : "",
      });
    } else {
      setForm({
        category: defaultCategory, client_id: initialClientId || "", dipendente: "", brand: "", brand_other: "",
        model: "", serial_number: "", notes: "", photos: [], documents: [], manual_ids: [],
        production_date: "", first_use_date: "", last_inspection_date: "", useful_life_years: "",
      });
    }
    setError("");
  }, [open, initial, defaultCategory, initialClientId]);

  if (!open) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  /** Resolve the current effective brand (dropdown value OR the "altro"
   * free-text input) for use in the similar-DPI lookup. */
  const effectiveBrand = () =>
    (form.brand === "altro" ? form.brand_other : form.brand).trim();

  /** When creating a NEW DPI, if the user has just filled BOTH brand and
   * model AND has not yet attached any photo/document, look up the most
   * recent DPI with the same brand+model and pre-fill photos + documents
   * from it (by reference — no file duplication). Silent, undoable via the
   * remove-photo / remove-document buttons. */
  const lookupSimilar = async () => {
    if (isEdit) return;
    const b = effectiveBrand();
    const m = (form.model || "").trim();
    if (!b || !m) return;
    if ((form.photos || []).length > 0 || (form.documents || []).length > 0) return;
    try {
      const params = new URLSearchParams({ brand: b, model: m });
      if (form.client_id) params.append("client_id", form.client_id);
      const { data } = await api.get(`/devices/similar?${params.toString()}`);
      const photos = data?.photos || [];
      const documents = (data?.documents || []).map((d) =>
        typeof d === "string" ? { path: d } : d
      );
      if (photos.length === 0 && documents.length === 0) return;
      setForm((f) => ({
        ...f,
        photos: (f.photos || []).length ? f.photos : photos,
        documents: (f.documents || []).length ? f.documents : documents,
      }));
      setSimilarNotice(
        `Trovato DPI ${b} ${m} già registrato — foto (${photos.length}) e documenti (${documents.length}) sono stati importati automaticamente.`
      );
    } catch {
      /* silent — feature is best-effort */
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.client_id) {
      setError("Seleziona un cliente");
      return;
    }
    setLoading(true);
    try {
      const resolvedBrand =
        form.brand === "altro" ? form.brand_other.trim() : form.brand || "";
      const payload = {
        kind,
        category: form.category,
        client_id: form.client_id,
        dipendente: isTool ? "" : form.dipendente,
        brand: resolvedBrand,
        model: form.model,
        serial_number: form.serial_number,
        notes: form.notes,
        photos: form.photos,
        documents: form.documents,
        manual_ids: form.manual_ids,
        production_date: form.production_date,
        first_use_date: form.first_use_date,
        last_inspection_date: form.last_inspection_date,
        useful_life_years: form.useful_life_years ? Number(form.useful_life_years) : null,
      };
      if (!isEdit) {
        // Strip empty optional fields on create
        Object.keys(payload).forEach((k) => {
          if (payload[k] === "" || payload[k] === null) delete payload[k];
        });
      }
      let data;
      if (isEdit) {
        ({ data } = await api.patch(`/devices/${initial.id}`, payload));
      } else {
        ({ data } = await api.post("/devices", payload));
      }
      setLoading(false);
      if (onSaved) onSaved(data);
      else if (onCreated) onCreated(data);
    } catch (err) {
      setLoading(false);
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
      data-testid="device-dialog"
      onClick={onClose}
    >
      <div className="bru-card w-full max-w-2xl p-6 mt-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-5">
          <div>
            <h3 className="text-2xl font-bold" >
              {isEdit ? (isTool ? "Modifica Strumento" : "Modifica DPI") : (isTool ? "Nuovo Strumento" : "Nuovo DPI")}
            </h3>
            {!isEdit && (
              <p className="text-xs text-neutral-500 mt-1">Il codice verrà generato automaticamente.</p>
            )}
          </div>
          <button onClick={onClose} className="bru-btn bru-btn-ghost !px-2 !py-1" data-testid="close-device-dialog">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!isEdit && clients.length === 0 && (
          <div className="border-[1.5px] border-black bg-[#ffcc00] text-black px-3 py-2 text-sm mb-4">
            Nessun cliente registrato. Crea prima un cliente dalla sezione <b>Clienti</b>.
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="bru-label">Cliente *</label>
              <select
                className="bru-input"
                required
                value={form.client_id}
                onChange={(e) => update("client_id", e.target.value)}
                data-testid="device-client-select"
              >
                <option value="">— Seleziona cliente —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="bru-label">Categoria *</label>
              <select
                className="bru-input"
                value={form.category}
                onChange={(e) => update("category", e.target.value)}
                data-testid="device-category-select"
              >
                {categoryOptions.map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          </div>

          {!isTool && (
            <div>
              <label className="bru-label">Dipendente assegnato</label>
              <input
                className="bru-input"
                value={form.dipendente}
                onChange={(e) => update("dipendente", e.target.value)}
                placeholder="Nome Cognome (opzionale)"
                data-testid="device-dipendente-input"
              />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="bru-label">Marca</label>
              <select
                className="bru-input"
                value={form.brand}
                onChange={(e) => update("brand", e.target.value)}
                onBlur={lookupSimilar}
                data-testid="device-brand-select"
              >
                <option value="">— Seleziona marca —</option>
                {BRAND_OPTIONS.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
                <option value="altro">Altro (specifica)</option>
              </select>
              {form.brand === "altro" && (
                <input
                  className="bru-input mt-2"
                  placeholder="Specifica marca"
                  value={form.brand_other}
                  onChange={(e) => update("brand_other", e.target.value)}
                  onBlur={lookupSimilar}
                  data-testid="device-brand-other-input"
                />
              )}
            </div>
            <div>
              <label className="bru-label">Modello</label>
              <input
                className="bru-input"
                value={form.model}
                onChange={(e) => update("model", e.target.value)}
                onBlur={lookupSimilar}
                placeholder="Es. Super Plasma HD"
                data-testid="device-model-input"
              />
            </div>
            <div>
              <label className="bru-label">Matricola</label>
              <input
                className="bru-input"
                value={form.serial_number}
                onChange={(e) => update("serial_number", e.target.value)}
                placeholder="SN-123456"
                data-testid="device-serial-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="bru-label">Data Produzione</label>
              <input
                type="date"
                min="1990-01-01"
                max="2099-12-31"
                className="bru-input"
                value={form.production_date}
                onChange={(e) => update("production_date", e.target.value)}
                data-testid="device-production-date-input"
              />
            </div>
            <div>
              <label className="bru-label">Data Primo Utilizzo</label>
              <input
                type="date"
                min="1990-01-01"
                max="2099-12-31"
                className="bru-input"
                value={form.first_use_date}
                onChange={(e) => update("first_use_date", e.target.value)}
                data-testid="device-first-use-date-input"
              />
            </div>
            <div>
              <label className="bru-label">Data Ultima Ispezione</label>
              <input
                type="date"
                min="1990-01-01"
                max="2099-12-31"
                className="bru-input"
                value={form.last_inspection_date}
                onChange={(e) => update("last_inspection_date", e.target.value)}
                data-testid="device-last-inspection-date-input"
              />
              <div className="text-[10px] text-neutral-500 mt-1">Opzionale · serve per registrare ispezioni pregresse</div>
            </div>
          </div>

          <div>
            <label className="bru-label">Vita Utile (anni)</label>
            <select
              className="bru-input max-w-[160px]"
              value={form.useful_life_years}
              onChange={(e) => update("useful_life_years", e.target.value)}
              data-testid="device-useful-life-select"
            >
              <option value="">— Seleziona —</option>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="bru-label">Note</label>
            <textarea
              className="bru-input"
              rows={3}
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              placeholder="Osservazioni..."
              data-testid="device-notes-input"
            />
          </div>
          <div>
            <label className="bru-label">Foto DPI (max 3)</label>
            <PhotoUploader value={form.photos} onChange={(p) => update("photos", p)} max={3} />
          </div>
          <div>
            <label className="bru-label">Documenti PDF (max 3)</label>
            <DocumentUploader value={form.documents} onChange={(arr) => update("documents", arr)} max={3} />
          </div>
          <div>
            <label className="bru-label">Manuali d&apos;uso</label>
            <ManualPicker kind="dpi" value={form.manual_ids} onChange={(ids) => update("manual_ids", ids)} />
          </div>
          {similarNotice && !error && (
            <div className="border-[1.5px] border-[#ff5a00] bg-[#fff7f0] text-black px-3 py-2 text-sm flex items-start gap-2" data-testid="device-similar-notice">
              <span className="text-lg leading-none">✓</span>
              <span className="flex-1">{similarNotice}</span>
              <button
                type="button"
                onClick={() => setSimilarNotice("")}
                className="text-neutral-500 hover:text-black shrink-0"
                aria-label="Chiudi avviso"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {error && (
            <div className="border-[1.5px] border-black bg-[#ff3b30] text-white px-3 py-2 text-sm" data-testid="device-error">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="bru-btn bru-btn-secondary" data-testid="cancel-device-button">
              Annulla
            </button>
            <button
              type="submit"
              disabled={loading || (!isEdit && clients.length === 0)}
              className="bru-btn bru-btn-primary disabled:opacity-60"
              data-testid="submit-device-button"
            >
              {loading ? "SALVATAGGIO…" : isEdit ? "AGGIORNA DPI" : "REGISTRA DPI"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
